import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import alphalogo from '../../../utils/alphahorizontal.png';

const createStyles = (primaryColor = '#005695', secondaryColor = '#0067b2') => {
  const colors = {
    primary: primaryColor,
    secondary: secondaryColor,
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      800: '#1F2937',
    },
    white: '#FFFFFF'
  };

  return StyleSheet.create({
    page: {
      backgroundColor: colors.white,
      padding: 30,
      paddingBottom: 60,
      fontFamily: 'Helvetica',
    },
    header: {
      backgroundColor: colors.white,
      padding: 15,
      borderRadius: 8,
      marginBottom: 15,
      borderWidth: 1,
      borderColor: colors.gray[200],
      // React-pdf no soporta boxShadow pero lo dejamos para mantener la apariencia original
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    },
    headerContent: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 20,
    },
    headerLogoContainer: {
      backgroundColor: colors.white,
      padding: 8,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: colors.gray[200],
      width: '30%',
    },
    headerLogo: {
      width: '100%',
      height: 40,
      objectFit: 'contain',
    },
    headerTitle: {
      backgroundColor: colors.primary,
      padding: '8 12',
      borderRadius: 6,
      width: '40%',
    },
    headerTitleText: {
      fontSize: 11,
      color: colors.white,
      fontWeight: 'bold',
      textAlign: 'center',
    },
    headerInfo: {
      width: '25%',
      gap: 6,
    },
    headerInfoItem: {
      backgroundColor: colors.gray[50],
      padding: '6 10',
      borderRadius: 4,
      marginBottom: 4,
    },
    value: {
      fontSize: 8,
      color: colors.gray[800],
    },

    // Footer
    footer: {
      position: 'absolute',
      bottom: 20,
      left: 30,
      right: 30,
      borderTopWidth: 1,
      borderColor: colors.gray[200],
      paddingTop: 5,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    footerLogo: {
      width: 120,
      height: 24,
      objectFit: 'contain',
    },
    footerWeb: {
      fontSize: 9,
      color: colors.primary,
      fontWeight: 'bold',
    },

    // Contenido principal (2 columnas)
    content: {
      flexDirection: 'row',
      gap: 20,
    },
    leftColumn: {
      width: '40%',
    },
    rightColumn: {
      width: '60%',
    },
    section: {
      backgroundColor: colors.white,
      padding: 5,
      borderRadius: 8,
      marginBottom: 8,
      borderWidth: 1,
      borderColor: colors.gray[200],
    },
    sectionTitle: {
      backgroundColor: colors.primary,
      padding: '8 12',
      borderRadius: 4,
      marginBottom: 5,
    },
    sectionTitleText: {
      fontSize: 10,
      color: colors.white,
      fontWeight: 'bold',
    },

    // Filas y Etiquetas
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 6,
      gap: 10,
    },
    label: {
      fontSize: 8,
      color: colors.gray[800],
      fontWeight: 'bold',
      width: '35%',
    },
    text: {
      fontSize: 8,
      color: colors.gray[800],
    },

    // Logo del prestador
    prestadorLogoContainer: {
      alignItems: 'center',
      marginBottom: 10,
    },
    prestadorLogo: {
      width: 110,
      height: 90,
      borderRadius: 6,
      objectFit: 'contain',
      borderWidth: 1,
      borderColor: colors.gray[200],
      backgroundColor: colors.gray[50],
      padding: 4,
    },

    // Ejecución
    executionDetails: {
      marginTop: 4,
    },
    timeRow: {
      flexDirection: 'column',
      gap: 6,
      marginBottom: 10,
    },
    timeItem: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.gray[50],
      padding: '6 8',
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.gray[200],
      marginBottom: 2,
    },
    observationsContainer: {
      gap: 4,
    },
    observationsLabel: {
      fontSize: 8,
      color: colors.gray[800],
      fontWeight: 'bold',
    },
    observationsBox: {
      backgroundColor: colors.gray[50],
      padding: 8,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.gray[200],
      minHeight: 60,
    },
    observationsText: {
      fontSize: 8,
      color: colors.gray[800],
      lineHeight: 1.4,
    },

    // Área
    areaInfo: {
      marginTop: 2,
    },

    // Actividades
    activitiesSection: {
      marginTop: 4,
    },
    activityItem: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.gray[50],
      padding: '6 8',
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.gray[200],
      marginBottom: 2,
      gap: 6,
    },
    activityLabel: {
      fontSize: 8,
      color: colors.gray[800],
      fontWeight: 'bold',
      width: '35%',
    },
    activityValue: {
      fontSize: 8,
      color: colors.gray[800],
      flex: 1,
    },

    // ===== Sección de Materiales (ajustada a tu pedido) =====
    materialsSection: {
      marginTop: 4,
    },
    // Encabezado de tabla
    materialsHeader: {
      flexDirection: 'row',
      backgroundColor: colors.primary,
      padding: '6 8',
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.gray[200],
      marginBottom: 2,
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    materialsHeaderText: {
      fontSize: 8,
      color: colors.white,
      fontWeight: 'bold',
      width: '25%',
      textAlign: 'center',
    },
    // Fila para cada material
    materialRow: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.gray[50],
      padding: '6 8',
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.gray[200],
      marginBottom: 2,
    },
    materialCell: {
      fontSize: 8,
      color: colors.gray[800],
      width: '25%',
      textAlign: 'center',
    },
    // Fila de total
    materialTotalRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      backgroundColor: colors.gray[50],
      padding: '6 8',
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.gray[200],
      marginTop: 4,
    },
    materialTotalLabel: {
      fontSize: 8,
      color: colors.gray[800],
      fontWeight: 'bold',
      marginRight: 8,
    },
    materialTotalValue: {
      fontSize: 8,
      color: colors.gray[800],
      fontWeight: 'bold',
    },

    // Sección de Personal
    personnelSection: {
      marginTop: 8,
    },
    personnelContainer: {
      flexDirection: 'row',
      gap: 2,
      backgroundColor: colors.white,
      padding: 2,
    },
    personnelHalf: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.white,
      padding: 2,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.gray[200],
      gap: 10,
    },
    signatureContainer: {
      width: '40%',
      height: 60,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.gray[50],
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.gray[200],
    },
    signatureImage: {
      width: '90%',
      height: '90%',
      objectFit: 'contain',
    },
    noSignatureText: {
      fontSize: 8,
      color: colors.gray[800],
      fontStyle: 'italic',
      textAlign: 'center',
    },
    personnelInfo: {
      flex: 1,
      gap: 4,
    },
    infoItem: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 4,
    },
    labelPersonnel: {
      fontSize: 10,
      color: colors.gray[800],
      fontWeight: 'bold',
      marginBottom: 4,
      textAlign: 'center',
    },
    roleText: {
      fontSize: 9,
      color: colors.primary,
      textAlign: 'center',
    },
  });
};


const ReporteMtoInfraPDF = ({ area, cliente, mto }) => {
  // Validación
  if (!area || !cliente || !mto) {
    return (
      <Document>
        <Page size="A4">
          <View>
            <Text>No hay información disponible</Text>
          </View>
        </Page>
      </Document>
    );
  }

  const styles = createStyles(cliente.primaryColor, cliente.secondaryColor);

  const logoPrestador =
    cliente.imagenurl ||
    'https://firebasestorage.googleapis.com/v0/b/alphaplatform.appspot.com/o/noImg.png?alt=media';

  // Footer
  const Footer = () => (
    <View style={styles.footer} fixed>
      <View>
        <Image src={alphalogo} style={styles.footerLogo} />
      </View>
      <View>
        <Text style={styles.footerWeb}>www.alphabiomedica.com</Text>
      </View>
    </View>
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* ========== HEADER ========== */}
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <View style={styles.headerLogoContainer}>
              <Image src={logoPrestador} style={styles.headerLogo} />
            </View>
            <View style={styles.headerTitle}>
              <Text style={styles.headerTitleText}>
                REPORTE DE MANTENIMIENTO DE INFRAESTRUCTURA
              </Text>
            </View>
            <View style={styles.headerInfo}>
              <View style={styles.headerInfoItem}>
                <Text style={styles.value}>
                  Reporte No: {(mto.progid || '').toString().slice(0, 6) || 'N/A'}
                </Text>
              </View>
              <View style={styles.headerInfoItem}>
                <Text style={styles.value}>
                  Fecha: {mto.fechainicio || 'N/A'}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* ===== CONTENIDO PRINCIPAL: 2 COLUMNAS ===== */}
        <View style={styles.content}>
          {/* ------ COLUMNA IZQUIERDA ------ */}
          <View style={styles.leftColumn}>
            {/* INFORMACIÓN DEL PRESTADOR */}
            <View style={styles.section}>
              <View style={styles.sectionTitle}>
                <Text style={styles.sectionTitleText}>INFORMACIÓN DEL PRESTADOR</Text>
              </View>
              <View style={styles.prestadorLogoContainer}>
                <Image src={logoPrestador} style={styles.prestadorLogo} />
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>NOMBRE:</Text>
                <Text style={styles.value}>{cliente.nombre}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>CC/NIT:</Text>
                <Text style={styles.value}>{cliente.ccnit}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>CIUDAD:</Text>
                <Text style={styles.value}>{cliente.ciudad}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>DIRECCIÓN:</Text>
                <Text style={styles.value}>{cliente.direccion}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>TELÉFONO:</Text>
                <Text style={styles.value}>{cliente.telefono}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>EMAIL:</Text>
                <Text style={styles.value}>{cliente.email}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>SEDE:</Text>
                <Text style={styles.value}>{cliente.sede}</Text>
              </View>
            </View>

            {/* DETALLES DE EJECUCIÓN */}
            <View style={styles.section}>
              <View style={styles.sectionTitle}>
                <Text style={styles.sectionTitleText}>DETALLES DE EJECUCIÓN</Text>
              </View>
              <View style={styles.executionDetails}>
                <View style={styles.timeRow}>
                  <View style={styles.timeItem}>
                    <Text style={styles.label}>FECHA INICIO:</Text>
                    <Text style={styles.value}>{mto.fechainicio || 'N/A'}</Text>
                  </View>
                  <View style={styles.timeItem}>
                    <Text style={styles.label}>HORA INICIO:</Text>
                    <Text style={styles.value}>{mto.horainicio || 'N/A'}</Text>
                  </View>
                  <View style={styles.timeItem}>
                    <Text style={styles.label}>FECHA FINAL:</Text>
                    <Text style={styles.value}>{mto.fechafinal || 'N/A'}</Text>
                  </View>
                  <View style={styles.timeItem}>
                    <Text style={styles.label}>HORA FINAL:</Text>
                    <Text style={styles.value}>{mto.horafinal || 'N/A'}</Text>
                  </View>
                </View>

                <View style={styles.observationsContainer}>
                  <Text style={styles.observationsLabel}>OBSERVACIONES:</Text>
                  <View style={styles.observationsBox}>
                    <Text style={styles.observationsText}>
                      {mto.observaciones || 'Ninguna'}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* ------ COLUMNA DERECHA ------ */}
          <View style={styles.rightColumn}>
            {/* INFORMACIÓN DEL ÁREA */}
            <View style={styles.section}>
              <View style={styles.sectionTitle}>
                <Text style={styles.sectionTitleText}>INFORMACIÓN DEL ÁREA</Text>
              </View>
              <View style={styles.areaInfo}>
                <View style={styles.row}>
                  <Text style={styles.label}>NOMBRE:</Text>
                  <Text style={styles.value}>{area.nombre}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>SERVICIO:</Text>
                  <Text style={styles.value}>{area.servicio}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>UBICACIÓN:</Text>
                  <Text style={styles.value}>{area.ubicacion}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>PISO:</Text>
                  <Text style={styles.value}>{area.piso || 'N/A'}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>ESTADO:</Text>
                  <Text style={styles.value}>{area.estado || 'N/A'}</Text>
                </View>
              </View>
            </View>

            {/* ACTIVIDADES DE MANTENIMIENTO */}
            <View style={styles.section}>
              <View style={styles.sectionTitle}>
                <Text style={styles.sectionTitleText}>ACTIVIDADES DE MANTENIMIENTO</Text>
              </View>
              <View style={styles.activitiesSection}>
                <View style={styles.activityItem}>
                  <Text style={styles.activityLabel}>Inst. Físicas:</Text>
                  <Text style={styles.activityValue}>
                    {mto.taskInstalaciones || 'No Registra'}
                  </Text>
                </View>
                <View style={styles.activityItem}>
                  <Text style={styles.activityLabel}>Red Hidráulica:</Text>
                  <Text style={styles.activityValue}>
                    {mto.taskHidraulica || 'No Registra'}
                  </Text>
                </View>
                <View style={styles.activityItem}>
                  <Text style={styles.activityLabel}>Red Eléctrica:</Text>
                  <Text style={styles.activityValue}>
                    {mto.taskElectrica || 'No Registra'}
                  </Text>
                </View>
                <View style={styles.activityItem}>
                  <Text style={styles.activityLabel}>Red de Gases:</Text>
                  <Text style={styles.activityValue}>
                    {mto.taskGases || 'No Registra'}
                  </Text>
                </View>
                <View style={styles.activityItem}>
                  <Text style={styles.activityLabel}>Comunicaciones:</Text>
                  <Text style={styles.activityValue}>
                    {mto.taskComunicaciones || 'No Registra'}
                  </Text>
                </View>
              </View>
            </View>

            {/* ================== MATERIALES UTILIZADOS ================== */}
            <View style={styles.section}>
              <View style={styles.sectionTitle}>
                <Text style={styles.sectionTitleText}>MATERIALES UTILIZADOS</Text>
              </View>

              {/* Encabezado de tabla */}
              <View style={styles.materialsHeader}>
                <Text style={styles.materialsHeaderText}>Material</Text>
                <Text style={styles.materialsHeaderText}>Cantidad</Text>
                <Text style={styles.materialsHeaderText}>Costo</Text>
                <Text style={styles.materialsHeaderText}>Subtotal</Text>
              </View>

              {/* Material 1 */}
              <View style={styles.materialRow}>
                <Text style={styles.materialCell}>
                  {mto.nombrerepuesto || 'No Registra'}
                </Text>
                <Text style={styles.materialCell}>{mto.cantidad || '0'}</Text>
                <Text style={styles.materialCell}>{mto.costo || '0'}</Text>
                {/* Subtotal: si tienes variable "subtotal", úsala; de lo contrario, haz tu cálculo */}
                <Text style={styles.materialCell}>{mto.subtotal || '0'}</Text>
              </View>

              {/* Material 2 */}
              <View style={styles.materialRow}>
                <Text style={styles.materialCell}>
                  {mto.nombrerepuesto1 || 'No Registra'}
                </Text>
                <Text style={styles.materialCell}>{mto.cantidad1 || '0'}</Text>
                <Text style={styles.materialCell}>{mto.costo1 || '0'}</Text>
                <Text style={styles.materialCell}>{mto.subtotal1 || '0'}</Text>
              </View>

              {/* Material 3 */}
              <View style={styles.materialRow}>
                <Text style={styles.materialCell}>
                  {mto.nombrerepuesto2 || 'No Registra'}
                </Text>
                <Text style={styles.materialCell}>{mto.cantidad2 || '0'}</Text>
                <Text style={styles.materialCell}>{mto.costo2 || '0'}</Text>
                <Text style={styles.materialCell}>{mto.subtotal2 || '0'}</Text>
              </View>

              {/* Fila de TOTAL (sacado de la variable mto.costo, según tu petición) */}
              <View style={styles.materialTotalRow}>
                <Text style={styles.materialTotalLabel}>TOTAL:</Text>
                <Text style={styles.materialTotalValue}>
                  {mto.totalrepuesto || '0'}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* ======= INFORMACIÓN DEL PERSONAL ======= */}
        <View style={styles.personnelSection}>
          <View style={styles.sectionTitle}>
            <Text style={styles.sectionTitleText}>INFORMACIÓN DEL PERSONAL</Text>
          </View>
          <View style={styles.personnelContainer}>
            {/* Persona que recibe */}
            <View style={styles.personnelHalf}>
              <View style={styles.signatureContainer}>
              {
  mto.metodoRecepcion === 'manual' ? (
    // Si es manual, mostramos firma del cliente (cliente.imagenfirma)
    cliente.imagenfirma ? (
      <Image src={cliente.imagenfirma} style={styles.signatureImage} />
    ) : (
      <Text style={styles.noSignatureText}>Pendiente firma</Text>
    )
  ) : (
    // Si NO es manual, usamos la firma almacenada en mto (mto.imagenfirma)
    mto.imagenfirma ? (
      <Image src={mto.imagenfirma} style={styles.signatureImage} />
    ) : (
      <Text style={styles.noSignatureText}>Pendiente firma</Text>
    )
  )
}
              </View>
              <View style={styles.personnelInfo}>
              {mto.metodoRecepcion==='usuario'?(<>
                <View style={styles.infoItem}>
                  <Text style={styles.labelPersonnel}>
                    {mto.datosUsuarioRecibe.nombre || ' Usuario N/A'}
                  </Text>
                  <Text style={styles.roleText}>
                    {mto.datosUsuarioRecibe.cargo || 'Cargo/rol'}
                  </Text>
                </View>
              </>):(<> <View style={styles.infoItem}>
                  <Text style={styles.labelPersonnel}>
                    {mto.firmarecibe || 'N/A'}
                  </Text>
                  <Text style={styles.roleText}>
                    {mto.cargorecibe || 'Cargo/rol'}
                  </Text>
                </View></>)}
               
              </View>
            </View>

            {/* Persona que realiza */}
            <View style={styles.personnelHalf}>
              <View style={styles.signatureContainer}>
                {mto.firmaIngeniero ? (
                  <Image src={mto.firmaIngeniero} style={styles.signatureImage} />
                ) : (
                  <Text style={styles.noSignatureText}>Pendiente firma</Text>
                )}
              </View>
              <View style={styles.personnelInfo}>
                <View style={styles.infoItem}>
                  <Text style={styles.labelPersonnel}>{mto.nombreIngeniero || 'N/A'}</Text>
                  <Text style={styles.roleText}>Responsable Mantenimiento</Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* FOOTER */}
        <Footer />
      </Page>
    </Document>
  );
};

export default ReporteMtoInfraPDF;
